import { getBackendUrl } from "@/utils/api";
import { useState, useEffect, useRef, useMemo } from "react";
import { resolveImageUrl } from "@/utils/imageUrl";

const API = getBackendUrl();

// Deterministic muted HSL colour derived from the image URL, so every image
// gets its own stable placeholder tone instead of a jarring universal grey.
// We deliberately keep saturation low + lightness high so the placeholder
// reads as "photo about to appear" rather than "coloured block".
function _urlHueColor(url) {
  if (!url) return "hsl(210, 12%, 82%)"; // neutral cool grey fallback
  let h = 5381;
  for (let i = 0; i < url.length; i++) h = ((h << 5) + h + url.charCodeAt(i)) | 0;
  return `hsl(${Math.abs(h) % 360}, 18%, 80%)`;
}

/**
 * Gets a low-resolution URL for progressive loading.
 * Returns a small, fast-loading version of any image.
 */
function getLowResUrl(src) {
  if (!src) return null;
  const resolved = resolveImageUrl(src);

  // Local uploads → use optimized endpoint at low quality
  if (src.startsWith("/api/uploads/")) {
    const filename = src.split("/").pop();
    return `${API}/api/img/${filename}?w=80&q=20`;
  }

  // Unsplash → append low-res params
  if (resolved.includes("unsplash.com")) {
    const sep = resolved.includes("?") ? "&" : "?";
    return `${resolved}${sep}w=80&q=20`;
  }

  // Rightmove images support a `_max_WxH` preset-resize suffix inserted
  // before the extension — only specific presets exist (others 404), and
  // 135x100 is the smallest confirmed-valid one (~20x smaller than full-res).
  if (resolved.includes("rightmove.co.uk")) {
    if (resolved.includes("_max_")) return null; // already a small preset (e.g. floorplans)
    const m = resolved.match(/^(.+)(\.(?:jpe?g|png|webp))$/i);
    return m ? `${m[1]}_max_135x100${m[2]}` : null;
  }

  // For any other external URL, return the same URL (browser will cache)
  return null;
}

/**
 * ProgressiveImg — shows a low-res image immediately, fades to full quality on load.
 *
 * Flow:
 *  1. Render low-res placeholder (visible, slightly blurred)
 *  2. Start loading full-res in background
 *  3. On full-res load → fade it in over the placeholder
 */
export default function ProgressiveImg({
  src,
  alt = "",
  className = "",
  style,
  loading = "lazy",
  sizes,
  srcSet,
  onLoad,
  fill = true,
  thumbnailSrc,
  ...rest
}) {
  const [hiResLoaded, setHiResLoaded] = useState(false);
  const [lowResLoaded, setLowResLoaded] = useState(false);
  const hiRef = useRef(null);
  const resolved = resolveImageUrl(src);
  // An explicit, already-known thumbnail (e.g. our own generated asset
  // thumbnail) is always preferred over the guessed/computed low-res URL.
  const lowRes = thumbnailSrc || getLowResUrl(src);
  // Stable per-URL colour — recomputed only when src changes.
  const placeholderColor = useMemo(() => _urlHueColor(resolved), [resolved]);

  // Reset when src changes
  useEffect(() => {
    setHiResLoaded(false);
    setLowResLoaded(false);
  }, [src]);

  // Detect if hi-res is already cached
  useEffect(() => {
    if (hiRef.current?.complete && hiRef.current?.naturalWidth > 0) {
      setHiResLoaded(true);
    }
  }, [resolved]);

  // `fill=false` — variable-size images (lightboxes) that rely on the
  // browser's natural intrinsic sizing (max-w/max-h + object-contain)
  // instead of filling a pre-sized parent box.
  if (!fill) {
    return (
      <div className="relative inline-block" style={{ backgroundColor: placeholderColor, ...style }}>
        {lowRes && !hiResLoaded && (
          <img
            src={lowRes}
            alt=""
            aria-hidden="true"
            className={`absolute inset-0 ${className}`}
            style={{ filter: "blur(8px)", transition: "opacity 0.3s ease", opacity: lowResLoaded ? 1 : 0 }}
            onLoad={() => setLowResLoaded(true)}
          />
        )}
        <img
          ref={hiRef}
          src={resolved}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          loading={loading}
          decoding="async"
          className={className}
          style={{ transition: "opacity 0.5s ease-in", opacity: lowRes ? (hiResLoaded ? 1 : 0) : 1 }}
          onLoad={() => { setHiResLoaded(true); onLoad?.(); }}
          {...rest}
        />
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{ backgroundColor: placeholderColor, ...style }}
    >
      {/* Layer 1: Low-res placeholder — loads fast, shows immediately */}
      {lowRes && !hiResLoaded && (
        <img
          src={lowRes}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            filter: "blur(8px)",
            transform: "scale(1.05)",
            transition: "opacity 0.3s ease",
            opacity: lowResLoaded ? 1 : 0
          }}
          onLoad={() => setLowResLoaded(true)}
        />
      )}

      {/* Layer 0 (coloured placeholder) is now handled by the wrapper's
          `backgroundColor`. We no longer animate-pulse a grey block — the
          deterministic colour reads as "photo about to appear" without the
          distracting shimmer, and the hi-res `<img>` fade-in on top gives
          plenty of "loading" signal on slow connections. */}

      {/* Layer 2: Full quality — fades in over the placeholder */}
      <img
        ref={hiRef}
        src={resolved}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        loading={loading}
        decoding="async"
        className="w-full h-full object-cover"
        style={{
          transition: "opacity 0.5s ease-in",
          opacity: lowRes ? (hiResLoaded ? 1 : 0) : 1
        }}
        onLoad={() => {
          setHiResLoaded(true);
          onLoad?.();
        }}
        {...rest}
      />
    </div>
  );
}
